import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Typography,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Tag,
  Tooltip,
  Dropdown
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faLayerGroup,
  faEdit,
  faTrash,
  faEllipsisV,
  faGripVertical,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import EventTypeGroupService from '../../services/EventTypeGroupService';
import EventTypeService from '../../services/EventTypeService';
import {
  ActionButton,
  BookingTable,
  BookingFilters,
  TabContentWrapper,
  AnimatedFiltersRow,
  AnimatedTableSection,
  StyledModal,
  FormSection
} from './NewStyled';
import HeartLoader from '../../components/shared/HeartLoader';

const { Title } = Typography;
const { TextArea } = Input;

const COLOR_OPTIONS = [
  { value: '#1890ff', label: 'Blue' },
  { value: '#e74c3c', label: 'Red' },
  { value: '#2ecc71', label: 'Green' },
  { value: '#f39c12', label: 'Orange' },
  { value: '#9b59b6', label: 'Purple' },
  { value: '#1abc9c', label: 'Teal' },
  { value: '#e91e63', label: 'Pink' },
  { value: '#607d8b', label: 'Gray' },
  { value: '#ff5722', label: 'Deep Orange' },
  { value: '#795548', label: 'Brown' }
];

const ICON_OPTIONS = [
  { value: 'calendar', label: 'Calendar' },
  { value: 'heart', label: 'Heart' },
  { value: 'stethoscope', label: 'Stethoscope' },
  { value: 'medkit', label: 'Med Kit' },
  { value: 'clipboard', label: 'Clipboard' },
  { value: 'users', label: 'Users' },
  { value: 'star', label: 'Star' },
  { value: 'check-circle', label: 'Check Circle' }
];

const EventTypeGroupManager = () => {
  const [groups, setGroups] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [form] = Form.useForm();
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [groupsRes, eventTypesRes] = await Promise.all([
        EventTypeGroupService.getGroups(),
        EventTypeService.getEventTypes()
      ]);

      if (groupsRes.success) {
        setGroups(groupsRes.data?.groups || []);
      } else {
        message.error('Failed to load groups');
      }

      if (eventTypesRes.success) {
        setEventTypes(eventTypesRes.data?.eventTypes || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      message.error('Unable to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingGroup(null);
    setSelectedEventTypes([]);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (group) => {
    const etIds = (group.eventTypes || []).map(et => et._id || et);
    setEditingGroup(group);
    setSelectedEventTypes(etIds);
    form.setFieldsValue({
      name: group.name,
      description: group.description || '',
      color: group.color || '#1890ff',
      icon: group.icon || 'calendar',
      isActive: group.isActive !== false,
      order: group.order || 0
    });
    setModalVisible(true);
  };

  const handleDelete = (group) => {
    Modal.confirm({
      title: 'Delete Group',
      content: `Are you sure you want to delete "${group.name}"? The event types in this group will become ungrouped but will NOT be deleted.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await EventTypeGroupService.deleteGroup(group._id);
          if (response.success) {
            message.success('Group deleted successfully');
            loadData();
          } else {
            message.error(response.error || 'Failed to delete group');
          }
        } catch (error) {
          console.error('Error deleting group:', error);
          message.error('Unable to delete group');
        }
      }
    });
  };

  const handleSubmit = async (values) => {
    try {
      const groupData = {
        name: values.name,
        description: values.description || '',
        color: values.color,
        icon: values.icon,
        isActive: values.isActive,
        order: values.order || 0,
        eventTypes: selectedEventTypes
      };

      let response;
      if (editingGroup) {
        response = await EventTypeGroupService.updateGroup(editingGroup._id, groupData);
      } else {
        response = await EventTypeGroupService.createGroup(groupData);
      }

      if (response.success) {
        message.success(`Group ${editingGroup ? 'updated' : 'created'} successfully`);
        setModalVisible(false);
        loadData();
      } else {
        message.error(response.error || `Failed to ${editingGroup ? 'update' : 'create'} group`);
      }
    } catch (error) {
      console.error('Error saving group:', error);
      message.error('Unable to save group');
    }
  };

  // Get event type IDs that are already assigned to other groups (not the current editing one)
  const getAssignedEventTypeIds = () => {
    const assigned = new Set();
    groups.forEach(group => {
      if (editingGroup && group._id === editingGroup._id) return;
      (group.eventTypes || []).forEach(et => {
        assigned.add(et._id || et);
      });
    });
    return assigned;
  };

  const filteredGroups = groups.filter(group => {
    if (!searchValue) return true;
    const search = searchValue.toLowerCase();
    return (
      group.name.toLowerCase().includes(search) ||
      (group.description && group.description.toLowerCase().includes(search))
    );
  });

  const columns = [
    {
      title: 'Group',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: (record.color || '#1890ff') + '20',
              color: record.color || '#1890ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              flexShrink: 0
            }}
          >
            <FontAwesomeIcon icon={faLayerGroup} />
          </div>
          <div>
            <div style={{ fontWeight: 600, color: '#1a1a1a' }}>{name}</div>
            {record.description && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {record.description}
              </div>
            )}
          </div>
        </div>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Event Types',
      dataIndex: 'eventTypes',
      key: 'eventTypes',
      render: (eventTypes) => {
        if (!eventTypes || eventTypes.length === 0) {
          return <Tag color="default">No event types</Tag>;
        }
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {eventTypes.slice(0, 3).map(et => (
              <Tag key={et._id} color={et.color || '#1890ff'} style={{ margin: 0 }}>
                {et.name}
              </Tag>
            ))}
            {eventTypes.length > 3 && (
              <Tooltip title={eventTypes.slice(3).map(et => et.name).join(', ')}>
                <Tag color="default" style={{ margin: 0 }}>+{eventTypes.length - 3} more</Tag>
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
      width: 80,
      align: 'center',
      render: (order) => (
        <span style={{ color: '#666' }}>
          <FontAwesomeIcon icon={faGripVertical} style={{ marginRight: '4px', color: '#bbb' }} />
          {order}
        </span>
      ),
      sorter: (a, b) => (a.order || 0) - (b.order || 0),
      defaultSortOrder: 'ascend'
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      align: 'center',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      align: 'center',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                label: 'Edit',
                icon: <FontAwesomeIcon icon={faEdit} />,
                onClick: () => handleEdit(record)
              },
              {
                key: 'delete',
                label: 'Delete',
                icon: <FontAwesomeIcon icon={faTrash} />,
                danger: true,
                onClick: () => handleDelete(record)
              }
            ]
          }}
          trigger={['click']}
        >
          <ActionButton
            type="text"
            size="small"
            style={{ padding: '4px 8px' }}
          >
            <FontAwesomeIcon icon={faEllipsisV} />
          </ActionButton>
        </Dropdown>
      )
    }
  ];

  const assignedIds = getAssignedEventTypeIds();

  return (
    <TabContentWrapper>
      <Row gutter={[0, 16]}>
        <Col span={24}>
          <AnimatedFiltersRow>
            <BookingFilters>
              <Row gutter={[8, 8]} align="middle" style={{ width: '100%' }}>
                <Col flex="auto" className="search-input">
                  <Input
                    placeholder="Search groups..."
                    prefix={<FontAwesomeIcon icon={faSearch} />}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col flex="none" className="filter-controls">
                  <ActionButton
                    type="primary"
                    icon={<FontAwesomeIcon icon={faPlus} style={{ marginRight: '6px' }} />}
                    onClick={handleCreate}
                  >
                    Create Group
                  </ActionButton>
                </Col>
              </Row>
            </BookingFilters>
          </AnimatedFiltersRow>
        </Col>

        <Col span={24}>
          <AnimatedTableSection style={{ position: 'relative' }}>
            {loading && (
              <div style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 10, minHeight: '200px'
              }}>
                <HeartLoader size={48} />
              </div>
            )}
            <BookingTable
              columns={columns}
              dataSource={filteredGroups}
              rowKey="_id"
              scroll={{ x: 600 }}
              pagination={{
                total: filteredGroups.length,
                pageSize: 10,
                showSizeChanger: false,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} groups`
              }}
              locale={{
                emptyText: (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                    <Title level={4} type="secondary">No Groups Yet</Title>
                    <p style={{ color: '#666', marginBottom: '24px' }}>
                      Group related event types together to organize your booking page
                    </p>
                    <ActionButton type="primary" icon={<FontAwesomeIcon icon={faPlus} />} onClick={handleCreate}>
                      Create Your First Group
                    </ActionButton>
                  </div>
                )
              }}
            />
          </AnimatedTableSection>
        </Col>
      </Row>

      <StyledModal
        title={editingGroup ? 'Edit Group' : 'Create Group'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        okText={editingGroup ? 'Update' : 'Create'}
        width={640}
        destroyOnClose
      >
        <FormSection>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              color: '#1890ff',
              icon: 'calendar',
              isActive: true,
              order: 0
            }}
          >
            <Form.Item
              name="name"
              label="Group Name"
              rules={[{ required: true, message: 'Please enter a group name' }]}
            >
              <Input 
                placeholder="e.g. Health Screenings" 
                maxLength={200} 
                size="large"
                style={{ height: '48px', fontSize: '16px' }}
              />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea
                placeholder="Brief description of what this group offers..."
                rows={3}
                maxLength={1000}
                showCount
                style={{ fontSize: '14px', lineHeight: '1.6', paddingTop: '10px' }}
              />
              <div style={{ marginTop: '6px', fontSize: '12px', color: '#999' }}>
                Displayed on the public booking page card
              </div>
            </Form.Item>

            <div style={{ 
              marginBottom: '24px', 
              padding: '20px', 
              background: '#f8f9fa', 
              borderRadius: '8px', 
              border: '1px solid #e9ecef' 
            }}>
              <h4 style={{ marginBottom: '16px', color: '#1a1a1a', fontSize: '14px', fontWeight: '600' }}>
                Appearance
              </h4>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="color" label="Color" style={{ marginBottom: 0 }}>
                    <Select size="large" style={{ height: '48px' }}>
                      {COLOR_OPTIONS.map(opt => (
                        <Select.Option key={opt.value} value={opt.value}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                              width: 14,
                              height: 14,
                              borderRadius: '50%',
                              backgroundColor: opt.value,
                              border: '1px solid rgba(0,0,0,0.1)',
                              flexShrink: 0
                            }} />
                            {opt.label}
                          </div>
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="icon" label="Icon" style={{ marginBottom: 0 }}>
                    <Select size="large" style={{ height: '48px' }}>
                      {ICON_OPTIONS.map(opt => (
                        <Select.Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ marginBottom: '8px', fontWeight: 500 }}>Event Types</div>
              <Select
                mode="multiple"
                placeholder="Search and select event types..."
                optionFilterProp="label"
                showSearch
                size="large"
                maxTagCount="responsive"
                style={{ width: '100%' }}
                popupMatchSelectWidth
                value={selectedEventTypes}
                onChange={setSelectedEventTypes}
                options={eventTypes.map(et => ({
                  value: et._id,
                  label: et.name,
                  disabled: assignedIds.has(et._id)
                }))}
                tagRender={({ label, closable, onClose }) => (
                  <Tag
                    closable={closable}
                    onClose={onClose}
                    style={{
                      marginRight: 4,
                      marginBottom: 2,
                      marginTop: 2,
                      borderRadius: '4px',
                      fontSize: '13px',
                      lineHeight: '24px',
                      padding: '0 8px',
                      background: '#f0f5ff',
                      border: '1px solid #adc6ff',
                      color: '#1d39c4'
                    }}
                  >
                    {label}
                  </Tag>
                )}
                optionRender={(option) => {
                  const et = eventTypes.find(e => e._id === option.value);
                  const isAssigned = assignedIds.has(option.value);
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '4px 0', opacity: isAssigned ? 0.5 : 1 }}>
                      <div style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: et?.color || '#1890ff',
                        flexShrink: 0
                      }} />
                      <span style={{ flex: 1 }}>{et?.name}</span>
                      {isAssigned && (
                        <span style={{ fontSize: '11px', color: '#999', whiteSpace: 'nowrap' }}>
                          Already grouped
                        </span>
                      )}
                    </div>
                  );
                }}
              />
              <div style={{ marginTop: '6px', fontSize: '12px', color: '#999' }}>
                Each event type can only belong to one group
              </div>
            </div>

            <Row gutter={16} align="middle">
              <Col span={12}>
                <Form.Item name="order" label="Display Order" style={{ marginBottom: 0 }}>
                  <InputNumber 
                    min={0} 
                    style={{ width: '100%', height: '48px' }} 
                    placeholder="0" 
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="isActive" label="Status" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </FormSection>
      </StyledModal>
    </TabContentWrapper>
  );
};

export default EventTypeGroupManager;
